import { DefaultModal, StyledText } from 'UI';
import { CourseDto } from 'api/generated';
import { AppConfig } from 'config';
import { useAsyncAction } from 'hooks';
import { useLottieJsonWithStatus } from 'hooks/useLottieJson';
import Lottie from 'lottie-react';
import React, { FC, useEffect, useState } from 'react';
import { actions, selectors, useAppSelector } from 'store';
import styled, { css } from 'styled-components';
import { respondToWidth } from 'styles/general/respondTo';
import { BaseModalProps } from 'types';
import { trackAmplitudeEvent } from 'utils/trackAmplitudeEvent';

// Import MVP animation files for bundle upgrade modal
import bogoMvpAnimation from '../../../assets/json/bogo/TNA_MVP.json';
// Import gold animations from constants
import { goldAnimatedMapping } from '../ChooseProductModal/components/Products/constants';

// Custom hook to detect mobile screen size
const useIsMobile = () => {
  const [isMobile, setIsMobile] = useState(false);

  useEffect(() => {
    const checkIsMobile = () => {
      setIsMobile(window.innerWidth <= 950);
    };

    checkIsMobile();
    window.addEventListener('resize', checkIsMobile);

    return () => window.removeEventListener('resize', checkIsMobile);
  }, []);

  return isMobile;
};

type BundleUpgradeOptionsModalProps = {
  bundle: CourseDto;
  bundleCourses: CourseDto[];
  onSelectOption: (selectedCourse: CourseDto) => void;
  isUpsellModal?: boolean; // New prop to indicate this is a course upsell modal
  upsellOrderIds?: string[];
} & BaseModalProps;

type UpsellStylingOverrides = {
  upsellPrimaryColor?: string;
  upsellHoverColor?: string;
  upsellBorderColor?: string;
  upsellCustomName?: string;
  upsellDescription?: string;
  upsellSubtitle?: string;
  upsellSubtext?: string;
  upsellBadgeBackground?: string;
  upsellBadgeText?: string;
  upsellAnimationUrl?: string;
  upsellAnimationKey?: string;
  upsellAnimation?: unknown;
};

type UpsellOption = CourseDto & UpsellStylingOverrides & { isBundle: boolean };

type BundleTitleItem = {
  name: string;
  color: string;
  iconUrl?: string;
};

const UpsellOptionCard: FC<{
  option: UpsellOption;
  index: number;
  colors: ColorScheme | BundleColorScheme;
  displayName: string;
  description: string;
  titleColor: string;
  iconUrl?: string;
  bundleTitleItems?: BundleTitleItem[];
  subtitle: string;
  subtext: string;
  badgeBackground?: string;
  badgeText?: string;
  fallbackAnimationData: unknown;
  onClick: () => void;
}> = ({
  option,
  index,
  colors,
  displayName,
  description,
  titleColor,
  iconUrl,
  bundleTitleItems,
  subtitle,
  subtext,
  badgeBackground,
  badgeText,
  fallbackAnimationData,
  onClick,
}) => {
  const upsellUrlRaw =
    typeof (option as any)?.upsellAnimationUrl === 'string' ? (option as any).upsellAnimationUrl : '';
  const upsellUrl = upsellUrlRaw.trim();
  const { data: urlAnimationData, status: urlStatus } = useLottieJsonWithStatus(upsellUrl);
  const animationData = upsellUrl
    ? urlStatus === 'loaded' && urlAnimationData
      ? urlAnimationData
      : urlStatus === 'error'
      ? fallbackAnimationData
      : null
    : fallbackAnimationData;

  const lottieKey = upsellUrl ? `upsell-url:${upsellUrl}:${urlStatus}` : 'upsell-default';

  return (
    <CourseCard
      key={`${option.id}-${option.name}-${index}`}
      onClick={onClick}
      colors={colors}
      isBundle={option.isBundle}>
      {option.isBundle && (
        <BadgeContainer>
          <Badge background={badgeBackground}>{badgeText || 'Limited Time BOGO Deal'}</Badge>
        </BadgeContainer>
      )}

      <IconContainer>
        <CourseIconLarge>
          {animationData ? (
            <Lottie key={lottieKey} animationData={animationData} style={{ width: '100px', height: '100px' }} />
          ) : (
            <div style={{ width: '100px', height: '100px' }} />
          )}
        </CourseIconLarge>
      </IconContainer>

      <TitleContainer>
        {option.isBundle && bundleTitleItems && bundleTitleItems.length > 0 ? (
          <TitleRow $wrap>
            {bundleTitleItems.map((item, itemIndex) => (
              <TitleItem key={`${option.id}-bundle-title-${itemIndex}`}>
                {item.iconUrl && <BundleTitleIcon src={item.iconUrl} alt={`${item.name} icon`} />}
                <CourseTitle isBundle={option.isBundle} $color={item.color}>
                  {item.name}
                </CourseTitle>
                {itemIndex < bundleTitleItems.length - 1 && <TitleSeparator>+</TitleSeparator>}
              </TitleItem>
            ))}
          </TitleRow>
        ) : (
          <TitleRow>
            <CourseTitle isBundle={option.isBundle} $color={titleColor}>
              {iconUrl && <TitleIcon src={iconUrl} alt={`${displayName} icon`} />}
              <TitleName>{displayName}</TitleName>
              {description && (
                <>
                  <TitleDash>-</TitleDash>
                  <TitleDescription dangerouslySetInnerHTML={{ __html: description }} />
                </>
              )}
            </CourseTitle>
          </TitleRow>
        )}
      </TitleContainer>

      <PriceContainer>
        <Price>{subtitle}</Price>
        <PriceSubtext>{subtext}</PriceSubtext>
      </PriceContainer>

      <ActionButton colors={colors} isBundle={option.isBundle}>
        {option.isBundle ? 'Get Bundle' : 'Get Started'}
      </ActionButton>
    </CourseCard>
  );
};

const BundleUpgradeOptionsModal: FC<BundleUpgradeOptionsModalProps> = ({
  bundle,
  bundleCourses: propBundleCourses, // Rename to avoid confusion
  onSelectOption,
  onClose,
  isUpsellModal = false, // Default to false for backward compatibility
  upsellOrderIds,
  ...props
}) => {
  const [getMyCoursesAction] = useAsyncAction(actions.courses.getMyCourses);
  const [getCourseByIdAction] = useAsyncAction(actions.courses.getCourseById);
  const [actualBundleCourses, setActualBundleCourses] = useState<CourseDto[]>([]);
  const [resolvedBundleTitleCourses, setResolvedBundleTitleCourses] = useState<CourseDto[] | null>(null);
  const isMobile = useIsMobile();

  // Get fresh courses from Redux store
  const allCourses = useAppSelector(selectors.courses.selectCourses);
  const direction = useAppSelector(selectors.direction.selectDirection);

  // Use actual bundle courses if loaded, otherwise fallback to props
  // For upsell modals, always use the specific courses passed in props
  const bundleCourses = isUpsellModal
    ? propBundleCourses
    : actualBundleCourses.length > 0
    ? actualBundleCourses
    : propBundleCourses;

  // Fetch fresh courses and bundle data when modal opens - only for non-upsell modals
  useEffect(() => {
    // Skip fetching for upsell modals since we have specific courses
    if (isUpsellModal) return;

    // Fetch the actual bundle courses if we have a bundle ID
    if (bundle?.id) {
      const loadCourses = async () => {
        try {
          // The Home load has already populated the store; refetching /courses/activatedByCustomer
          // here blocked the modal for its full cost and the result was never read anyway
          // (`allCourses` was captured at render time, BEFORE the await resolved — a stale
          // closure). Fetch only when the store is genuinely empty (cold deep-link), and then use
          // the action's resolved return value rather than the closure.
          const currentCourses = allCourses && allCourses.length > 0 ? allCourses : await getMyCoursesAction();
          if (currentCourses && currentCourses.length > 0) {
            const nonBundleCourses = currentCourses.filter((course) => (course.type as string) !== 'bundle');
            setActualBundleCourses(nonBundleCourses);
          } else {
            setActualBundleCourses(propBundleCourses);
          }
        } catch (error) {
          setActualBundleCourses(propBundleCourses);
        }
      };
      loadCourses();
    }
  }, [bundle?.id, isUpsellModal]); // Include isUpsellModal in dependencies

  useEffect(() => {
    let cancelled = false;

    const loadBundleTitleCourses = async () => {
      if (!bundle?.id) {
        setResolvedBundleTitleCourses(null);
        return;
      }

      try {
        let coursesFromPublicRelation: CourseDto[] = [];
        try {
          const baseApiUrl = (AppConfig.BASE_API_URL || '').replace(/\/+$/, '');
          const publicUrl = `${baseApiUrl}/courses/${bundle.id}/public`;
          const response = await fetch(publicUrl, {
            headers: {
              'Content-Type': 'application/json',
              'Cache-Control': 'no-cache',
              Pragma: 'no-cache',
            },
            cache: 'no-store',
          });

          if (response.ok) {
            const bundlePublicCourseData = await response.json();
            const bundleCoursesAsBundle =
              (bundlePublicCourseData as CourseDto & { bundleCoursesAsBundle?: Array<{ course?: CourseDto }> })
                ?.bundleCoursesAsBundle || [];

            coursesFromPublicRelation = bundleCoursesAsBundle
              .map((item: { course?: CourseDto }) => item?.course)
              .filter((course: CourseDto | undefined): course is CourseDto => Boolean(course))
              .filter((course: CourseDto) => (course.type as string) !== 'bundle');
          }
        } catch (_publicFetchError) {
          coursesFromPublicRelation = [];
        }

        // The private fetch exists as a merge fallback; when the public relation already
        // returned the members (the dedupe below prefers those rows anyway), the second
        // sequential round trip adds nothing but latency.
        let coursesFromPrivateRelation: CourseDto[] = [];
        if (coursesFromPublicRelation.length === 0) {
          try {
            const privateBundleCourse = (await getCourseByIdAction(bundle.id)) as
              | (CourseDto & { bundleCoursesAsBundle?: Array<{ course?: CourseDto }> })
              | undefined;

            const privateBundleMembers = privateBundleCourse?.bundleCoursesAsBundle || [];
            coursesFromPrivateRelation = privateBundleMembers
              .map((item: { course?: CourseDto }) => item?.course)
              .filter((course: CourseDto | undefined): course is CourseDto => Boolean(course))
              .filter((course: CourseDto) => (course.type as string) !== 'bundle');
          } catch (_privateFetchError) {
            coursesFromPrivateRelation = [];
          }
        }

        const mergedResolvedCourses = [...coursesFromPublicRelation, ...coursesFromPrivateRelation];
        const seenIds = new Set<string>();
        const dedupedResolvedCourses = mergedResolvedCourses.filter((course) => {
          if (!course?.id) return false;
          if (seenIds.has(course.id)) return false;
          seenIds.add(course.id);
          return true;
        });

        if (!cancelled) {
          setResolvedBundleTitleCourses(dedupedResolvedCourses.length > 0 ? dedupedResolvedCourses : null);
        }
      } catch (_error) {
        if (!cancelled) setResolvedBundleTitleCourses(null);
      }
    };

    loadBundleTitleCourses();

    return () => {
      cancelled = true;
    };
  }, [bundle?.id]);

  const handleSelectOption = (option: CourseDto, isBundleOption: boolean) => {
    if (isBundleOption) {
      trackAmplitudeEvent('Bundle Upgrade Option Selected', {
        bundleId: option.id,
        bundleName: option.name,
      });
    } else {
      trackAmplitudeEvent('Individual Course Upgrade Option Selected', {
        courseId: option.id,
        courseName: option.name,
      });
    }

    onSelectOption(option);
    onClose();
  };

  // Helper function to get course animation based on course name
  const getCourseAnimation = (courseName: string) => {
    if (courseName.toLowerCase().includes('cpt') || courseName.toLowerCase().includes('personal trainer')) {
      // For CPT courses, use gold animation based on user's actual direction
      const directionId = direction?.id;
      return directionId && goldAnimatedMapping[directionId as keyof typeof goldAnimatedMapping]
        ? goldAnimatedMapping[directionId as keyof typeof goldAnimatedMapping]
        : goldAnimatedMapping['451553b1-e21e-4221-b248-e160177536e6']; // fallback to default
    }
    // For bundle/other courses, use BOGO MVP animation
    return bogoMvpAnimation;
  };

  // Helper function to get course color scheme based on course name
  const getCourseColors = (courseName: string) => {
    const lowerName = courseName.toLowerCase();

    if (lowerName.includes('cpt') || lowerName.includes('personal trainer') || lowerName.includes('trainer')) {
      return {
        primary: '#ED5A26',
        hover: '#D14A1F',
        border: '#ED5A26',
      };
    }
    return {
      primary: '#6B7280',
      hover: '#4B5563',
      border: '#6B7280',
    };
  };

  const bundleColors = {
    primary: 'linear-gradient(to right, #ED5A26, #1CBF73)',
    hover: 'linear-gradient(to right, #D14A1F, #18A866)',
    border: 'linear-gradient(to right, #ED5A26, #1CBF73)',
  };

  const getOptionColors = (option: UpsellOption) => {
    const base = option.isBundle ? bundleColors : getCourseColors(option.name);

    return {
      primary: option.upsellPrimaryColor || base.primary,
      hover: option.upsellHoverColor || base.hover,
      border: option.upsellBorderColor || base.border,
    };
  };

  const getOptionTitleColor = (option: UpsellOption, colors: ColorScheme | BundleColorScheme) => {
    const raw = typeof option.upsellPrimaryColor === 'string' ? option.upsellPrimaryColor.trim() : '';
    if (raw && !raw.includes('gradient')) return raw;

    const fallback = colors.primary || '#374151';
    return typeof fallback === 'string' && !fallback.includes('gradient')
      ? fallback
      : getCourseColors(option.name).primary;
  };

  const getOptionDisplayName = (option: UpsellOption) => {
    const custom = typeof option.upsellCustomName === 'string' ? option.upsellCustomName.trim() : '';
    return isUpsellModal && custom ? custom : option.name;
  };

  const getBundleDisplayName = (option: UpsellOption) => {
    const custom = typeof option.upsellCustomName === 'string' ? option.upsellCustomName.trim() : '';
    return custom || option.name;
  };

  const getOptionDescription = (option: UpsellOption) => {
    const description = typeof option.upsellDescription === 'string' ? option.upsellDescription.trim() : '';
    return isUpsellModal ? description : '';
  };

  const getOptionIconUrl = (option: UpsellOption) => {
    const icon = typeof option.courseIconUrl === 'string' ? option.courseIconUrl.trim() : '';
    const logo = typeof option.courseLogoUrl === 'string' ? option.courseLogoUrl.trim() : '';
    return icon || logo || undefined;
  };

  const getCourseTitleColor = (course: Partial<UpsellOption> & { name?: string }) => {
    const raw = typeof course.upsellPrimaryColor === 'string' ? course.upsellPrimaryColor.trim() : '';
    if (raw && !raw.includes('gradient')) return raw;
    const name = typeof course.name === 'string' ? course.name : '';
    return getCourseColors(name).primary;
  };

  const getIconUrlFromCourse = (course: Partial<UpsellOption>) => {
    const icon = typeof course.courseIconUrl === 'string' ? course.courseIconUrl.trim() : '';
    const logo = typeof course.courseLogoUrl === 'string' ? course.courseLogoUrl.trim() : '';
    return icon || logo || undefined;
  };

  const getOptionAnimation = (option: UpsellOption) => {
    const key = typeof option.upsellAnimationKey === 'string' ? option.upsellAnimationKey.trim() : '';
    if (key) {
      switch (key) {
        case 'bogo_mvp':
          return bogoMvpAnimation;
        case 'direction_gold': {
          const directionId = direction?.id;
          return directionId && goldAnimatedMapping[directionId as keyof typeof goldAnimatedMapping]
            ? goldAnimatedMapping[directionId as keyof typeof goldAnimatedMapping]
            : goldAnimatedMapping['451553b1-e21e-4221-b248-e160177536e6'];
        }
        default:
          break;
      }
    }

    return option.upsellAnimation || (option.isBundle ? bogoMvpAnimation : getCourseAnimation(option.name));
  };

  const getOptionSubtitle = (option: UpsellOption) => {
    if (option.upsellSubtitle) return option.upsellSubtitle;
    return option.isBundle ? 'Buy One, Get One (BOGO)' : 'Starting at $399';
  };

  const getOptionBadgeBackground = (option: UpsellOption) => {
    if (!option.isBundle) return undefined;
    return option.upsellBadgeBackground || 'linear-gradient(to right, #ed5a26, #1cbf73)';
  };

  const getOptionBadgeText = (option: UpsellOption) => {
    if (!option.isBundle) return undefined;
    const text = typeof option.upsellBadgeText === 'string' ? option.upsellBadgeText.trim() : '';
    return text || 'Limited Time BOGO Deal';
  };

  const formatPriceAmount = (amount: number) => {
    return Number.isInteger(amount) ? amount.toString() : amount.toFixed(2);
  };

  const getInstallmentAmount = (option: UpsellOption) => {
    const installment = option.prices?.installment;
    if (!installment || typeof installment.amount !== 'number') return null;

    const defaultDiscount = installment.defaultCoupon?.discountAmount || 0;
    const firstMonthDiscount = installment.firstMonthDiscountCoupon?.discountAmount || 0;
    const monthlyPrice = Math.round((installment.amount - defaultDiscount) / 12);
    const firstMonthPrice = monthlyPrice - Math.round(firstMonthDiscount);

    return firstMonthPrice;
  };

  const getMonthlySubscriptionAmount = (option: UpsellOption) => {
    const monthly = option.prices?.monthlySubscriptions;
    if (!monthly) return null;

    const amounts = [monthly.bronze, monthly.silver, monthly.gold]
      .map((price) => {
        if (!price || typeof price.amount !== 'number') return null;
        const discount = price.defaultCoupon?.discountAmount || 0;
        return price.amount - discount;
      })
      .filter((value): value is number => typeof value === 'number');

    return amounts.length ? Math.min(...amounts) : null;
  };

  const getOptionSubtext = (option: UpsellOption) => {
    const override = typeof option.upsellSubtext === 'string' ? option.upsellSubtext.trim() : '';
    if (override) return override;

    const installmentAmount = getInstallmentAmount(option);
    if (installmentAmount !== null) {
      const amountText = `$${formatPriceAmount(installmentAmount)}`;
      return option.isBundle
        ? `Get started for just ${amountText} – 0% interest, no credit check`
        : `Get started for just ${amountText} down – 0% interest, no credit check`;
    }

    const subscriptionAmount = getMonthlySubscriptionAmount(option);
    if (subscriptionAmount !== null) {
      return `Get started for just $${formatPriceAmount(subscriptionAmount)}/mo – 0% interest, no credit check`;
    }

    return option.isBundle
      ? 'Get started for just $1 – 0% interest, no credit check'
      : 'Get started for just $39 down – 0% interest, no credit check';
  };
  // Normalize options: detect bundles by course type
  const courseOptions: UpsellOption[] = bundleCourses.map((course) => ({
    ...(course as UpsellOption),
    isBundle: (course.type as string) === 'bundle',
  }));
  const bundleOption: UpsellOption = {
    ...(bundle as UpsellOption),
    isBundle: (bundle.type as string) === 'bundle',
  };

  const bundleTitleCourses: UpsellOption[] = (() => {
    const fromResolved = Array.isArray(resolvedBundleTitleCourses)
      ? resolvedBundleTitleCourses
          .map((course) => ({
            ...(course as UpsellOption),
            isBundle: (course.type as string) === 'bundle',
          }))
          .filter((course) => !course.isBundle)
      : [];

    const bundleCoursesFromBundle = (bundle as CourseDto & { bundleCoursesAsBundle?: Array<{ course?: CourseDto }> })
      .bundleCoursesAsBundle;
    const fromBundleRelation = Array.isArray(bundleCoursesFromBundle)
      ? bundleCoursesFromBundle
          .map((item) => item?.course)
          .filter((course): course is CourseDto => Boolean(course))
          .map((course) => ({
            ...(course as UpsellOption),
            isBundle: (course.type as string) === 'bundle',
          }))
          .filter((course) => !course.isBundle)
      : [];

    const fromOptions = courseOptions.filter((course) => !course.isBundle);

    const bundleNameTokens = (bundle?.name || '')
      .split('+')
      .map((token) => token.trim())
      .filter(Boolean);

    const fromBundleNameMatches = bundleNameTokens
      .map((token) => {
        const normalizedToken = token.toLowerCase();
        return (allCourses || []).find((course) => {
          if (!course?.id) return false;
          if ((course.type as string) === 'bundle') return false;
          return (course.name || '').toLowerCase().includes(normalizedToken);
        });
      })
      .filter((course): course is CourseDto => Boolean(course))
      .map((course) => ({
        ...(course as UpsellOption),
        isBundle: (course.type as string) === 'bundle',
      }))
      .filter((course) => !course.isBundle);

    const merged = [...fromResolved, ...fromBundleRelation, ...fromOptions, ...fromBundleNameMatches];
    const seenIds = new Set<string>();

    return merged.filter((course) => {
      if (!course?.id) return false;
      if (seenIds.has(course.id)) return false;
      seenIds.add(course.id);
      return true;
    });
  })();

  const getBundleTitleItemsForOption = (option: UpsellOption): BundleTitleItem[] => {
    const sourceCourses: UpsellOption[] = (() => {
      if (option.id === bundle.id) {
        return bundleTitleCourses;
      }

      const optionBundleCourses =
        (option as CourseDto & { bundleCoursesAsBundle?: Array<{ course?: CourseDto }> }).bundleCoursesAsBundle || [];

      const fromRelation = optionBundleCourses
        .map((item) => item?.course)
        .filter((course): course is CourseDto => Boolean(course))
        .map((course) => ({
          ...(course as UpsellOption),
          isBundle: (course.type as string) === 'bundle',
        }))
        .filter((course) => !course.isBundle);

      if (fromRelation.length > 0) {
        return fromRelation;
      }

      const optionNameTokens = (option?.name || '')
        .split('+')
        .map((token) => token.trim())
        .filter(Boolean);

      const fromNameMatches = optionNameTokens
        .map((token) => {
          const normalizedToken = token.toLowerCase();
          return (allCourses || []).find((course) => {
            if (!course?.id) return false;
            if ((course.type as string) === 'bundle') return false;
            return (course.name || '').toLowerCase().includes(normalizedToken);
          });
        })
        .filter((course): course is CourseDto => Boolean(course))
        .map((course) => ({
          ...(course as UpsellOption),
          isBundle: (course.type as string) === 'bundle',
        }))
        .filter((course) => !course.isBundle);

      return fromNameMatches;
    })();

    const seenIds = new Set<string>();
    const dedupedCourses = sourceCourses.filter((course) => {
      if (!course?.id) return false;
      if (seenIds.has(course.id)) return false;
      seenIds.add(course.id);
      return true;
    });

    return dedupedCourses.map((course) => ({
      name: getBundleDisplayName(course),
      color: getCourseTitleColor(course),
      iconUrl: getIconUrlFromCourse(course),
    }));
  };

  const sortByUpsellOrder = (options: UpsellOption[]) => {
    if (!isUpsellModal || !upsellOrderIds || upsellOrderIds.length === 0) {
      return options;
    }

    const orderIndex = new Map<string, number>(upsellOrderIds.map((id, index) => [id, index]));
    return [...options].sort((a, b) => {
      const aIndex = orderIndex.get(a.id);
      const bIndex = orderIndex.get(b.id);

      if (aIndex === undefined && bIndex === undefined) return 0;
      if (aIndex === undefined) return 1;
      if (bIndex === undefined) return -1;
      return aIndex - bIndex;
    });
  };

  // For upsell modals, preserve the order from admin-configured IDs when available.
  // For regular bundle upgrade modals, keep legacy ordering (bundle last on desktop, first on mobile).
  const baseOptions = isUpsellModal
    ? [bundleOption, ...courseOptions]
    : isMobile
    ? [bundleOption, ...courseOptions]
    : [...courseOptions, bundleOption];
  const allOptions = sortByUpsellOrder(baseOptions);

  console.log('🔍 [BundleUpgradeOptionsModal] options', {
    isUpsellModal,
    bundleId: bundleOption.id,
    bundleName: bundleOption.name,
    upsellOrderIds,
    bundleCourses: courseOptions.map((course) => ({
      id: course.id,
      name: course.name,
      type: course.type,
      upsellCustomName: course.upsellCustomName,
    })),
    allOptionIds: allOptions.map((option) => option.id),
  });

  const mobileContainerCSS = css`
    ${respondToWidth.sm`
      padding: 20px 24px;
    `}
  `;

  const mobileHeaderCSS = css`
    ${respondToWidth.sm`
      justify-content: center !important;
      margin-bottom: 32px;
      margin-top: 40px !important;
      padding-top: 40px !important;
    `}
  `;

  return (
    <DefaultModal
      onRequestClose={onClose}
      title="Choose Your Upgrade Option"
      containerCSS={mobileContainerCSS}
      headerContainerCSS={mobileHeaderCSS}
      {...props}>
      <ModalContentWrapper>
        <Container>
          <Description>
            <StyledText font="body_large_regular_16">
              {isUpsellModal
                ? 'Upgrade your current course or save more with our recommended bundle'
                : 'Choose between individual certifications or save big with our limited-time BOGO bundle deal'}
            </StyledText>
          </Description>

          <GridContainer optionCount={allOptions.length}>
            {allOptions.map((option, index) => {
              const optionColors = getOptionColors(option);

              return (
                <UpsellOptionCard
                  key={`${option.id}-${option.name}-${index}`}
                  option={option}
                  index={index}
                  colors={optionColors}
                  displayName={getOptionDisplayName(option)}
                  description={getOptionDescription(option)}
                  titleColor={getOptionTitleColor(option, optionColors)}
                  iconUrl={getOptionIconUrl(option)}
                  bundleTitleItems={option.isBundle ? getBundleTitleItemsForOption(option) : undefined}
                  subtitle={getOptionSubtitle(option)}
                  subtext={getOptionSubtext(option)}
                  badgeBackground={getOptionBadgeBackground(option)}
                  badgeText={getOptionBadgeText(option)}
                  fallbackAnimationData={getOptionAnimation(option)}
                  onClick={() => handleSelectOption(option, option.isBundle)}
                />
              );
            })}
          </GridContainer>
        </Container>
      </ModalContentWrapper>
    </DefaultModal>
  );
};

export default BundleUpgradeOptionsModal;

const ModalContentWrapper = styled.div`
  /* Override modal title styling - target all possible title elements */
  h1,
  h2,
  h3,
  h4,
  h5,
  h6,
  [class*='title'],
  [class*='Title'],
  [class*='header'],
  [class*='Header'] {
    color: black !important;
    margin-bottom: 8px !important;
  }

  /* Target parent containers that might have the title */
  > * h1,
  > * h2,
  > * h3,
  > * h4,
  > * h5,
  > * h6 {
    color: black !important;
    margin-bottom: 8px !important;
  }

  /* Global override for any text that might be the title */
  * {
    &[role='heading'] {
      color: black !important;
      margin-bottom: 8px !important;
    }
  }
`;

const Container = styled.div`
  display: flex;
  flex-direction: column;
  gap: 8px;
  padding: 8px 24px 0;
  max-width: 1200px;
  width: 100%;
  overflow: visible;

  /* Reduce padding on mobile */
  @media (max-width: 950px) {
    padding: 8px 16px 0;
  }
`;

const Description = styled.div`
  text-align: center;
  margin-bottom: 0;
`;

const GridContainer = styled.div<{ optionCount: number }>`
  display: grid;
  gap: 24px;
  max-width: 100%;
  margin: 0 auto;
  align-items: stretch;
  padding: 16px 0 24px;
  overflow: visible;

  /* Desktop: adjust layout based on number of options */
  @media (min-width: 951px) {
    ${({ optionCount }) => {
      if (optionCount <= 1) {
        return css`
          grid-template-columns: 1fr;
          max-width: 520px;
          justify-content: center;
        `;
      }

      if (optionCount === 2) {
        return css`
          grid-template-columns: 1fr 1fr;
          max-width: 800px;
          justify-content: center;
        `;
      }

      // 3+ options: keep them on one row
      return css`
        grid-template-columns: 1fr 1fr 1fr;
        max-width: 1200px;
      `;
    }}
  }

  /* Mobile: stacked layout with reduced padding */
  @media (max-width: 950px) {
    grid-template-columns: 1fr;
    gap: 16px;
    padding: 8px 0 16px;
  }
`;

interface ColorScheme {
  primary: string;
  hover: string;
  border: string;
}

interface BundleColorScheme {
  primary: string;
  hover: string;
  border: string;
}

const CourseCard = styled.div<{ colors: ColorScheme | BundleColorScheme; isBundle: boolean }>`
  ${({ colors, isBundle }) => css`
    position: relative;
    background: ${colors.border};
    border: ${isBundle ? '2px solid transparent' : 'none'};
    border-radius: 16px;
    box-shadow: 0 8px 25px rgba(0, 0, 0, 0.1);
    padding: ${isBundle ? '0' : '2px'};
    overflow: visible;
    cursor: pointer;
    transition: all 0.3s ease;
    display: flex;
    flex-direction: column;
    margin: ${isBundle ? '16px 8px 8px' : '16px 0 0'};

    &::before {
      content: '';
      position: absolute;
      top: 2px;
      left: 2px;
      right: 2px;
      bottom: 2px;
      background: white;
      border-radius: 14px;
      z-index: 0;
    }

    > * {
      position: relative;
      z-index: 1;
    }

    ${isBundle &&
    css`
      transform: scale(1.02);
      box-shadow: 0 12px 35px rgba(0, 0, 0, 0.15);
      z-index: 10;
    `}

    &:hover {
      transform: ${isBundle ? 'scale(1.05)' : 'scale(1.02)'};
      box-shadow: 0 16px 40px rgba(0, 0, 0, 0.2);
    }

    @media (max-width: 950px) {
      margin: ${isBundle ? '16px 0 8px' : '16px 0 0'};
      transform: ${isBundle ? 'scale(1.02)' : 'scale(1)'};

      &:hover {
        transform: scale(1.02);
      }
    }
  `}
`;

const BadgeContainer = styled.div`
  position: absolute;
  top: -12px;
  left: 50%;
  transform: translateX(-50%);
  z-index: 20;
`;

const Badge = styled.div<{ background?: string }>`
  background: ${({ background }) => background || 'linear-gradient(to right, #ed5a26, #1cbf73)'};
  color: white;
  padding: 8px 24px;
  border-radius: 9999px;
  font-size: 14px;
  font-weight: bold;
  box-shadow: 0 4px 12px rgba(0, 0, 0, 0.1);
  white-space: nowrap;
`;

const IconContainer = styled.div`
  display: flex;
  justify-content: center;
  align-items: center;
  padding: 32px 0 16px;
  height: 160px;

  @media (max-width: 950px) {
    padding: 28px 0 16px;
    height: 150px;
    margin-bottom: 8px;
  }
`;

const CourseIconLarge = styled.div`
  width: 100px;
  height: 100px;
  display: flex;
  align-items: center;
  justify-content: center;
`;

const TitleContainer = styled.div`
  text-align: center;
  padding: 0 24px 0px;
  min-height: 40px;
  display: flex;
  align-items: center;
  justify-content: center;

  @media (max-width: 950px) {
    padding: 0 16px 8px;
    min-height: 45px;
    margin-bottom: 8px;
  }
`;

const TitleRow = styled.div<{ $wrap?: boolean }>`
  display: inline-flex;
  align-items: center;
  gap: 8px;
  max-width: 100%;
  flex-wrap: ${({ $wrap }) => ($wrap ? 'wrap' : 'nowrap')};
  justify-content: center;
`;

const TitleItem = styled.div`
  display: inline-flex;
  align-items: center;
  gap: 6px;
`;

const TitleName = styled.span`
  display: inline-flex;
  align-items: baseline;
`;

const TitleDash = styled.span`
  margin: 0 2px;
`;

const TitleDescription = styled.span`
  display: inline;
  line-height: 1.2;
`;

const TitleSeparator = styled.span`
  font-weight: 900;
  margin-left: 6px;
  margin-right: 2px;
  color: #000 !important;
`;

const TitleIcon = styled.img`
  width: 26px;
  height: 26px;
  object-fit: contain;
  flex-shrink: 0;
  vertical-align: middle;
  margin-right: 6px;
`;

const BundleTitleIcon = styled(TitleIcon)`
  margin-right: 0;
`;

const CourseTitle = styled.span<{ isBundle: boolean; $color: string }>`
  ${({ $color }) => css`
    font-size: 20px;
    font-weight: 900;
    text-transform: uppercase;
    line-height: 1.2;
    margin: 0;
    color: ${$color} !important;
    display: inline-block;
    text-align: center;
    max-width: 100%;
    word-break: break-word;

    @media (max-width: 950px) {
      font-size: 18px;
      line-height: 1.1;
    }
  `}
`;

const PriceContainer = styled.div`
  text-align: center;
  padding: 0 24px 24px;
  height: 100px;
  display: flex;
  flex-direction: column;
  justify-content: center;
  flex-grow: 1;

  @media (max-width: 950px) {
    padding: 8px 16px 24px;
    height: 100px;
    margin-bottom: 8px;
  }
`;

const Price = styled.div`
  font-size: 20px;
  font-weight: 900;
  color: #000;
  margin-bottom: 8px;
`;

const PriceSubtext = styled.div`
  font-size: 14px;
  color: #6b7280;
  font-weight: 500;

  span {
    font-weight: bold;
  }
`;

const ActionButton = styled.button<{ colors: ColorScheme | BundleColorScheme; isBundle: boolean }>`
  ${({ colors, isBundle }) => css`
    width: calc(100% - 48px);
    margin: 0 24px 24px;
    padding: 16px;
    border-radius: 12px;
    border: none;
    font-weight: bold;
    font-size: 18px;
    cursor: pointer;
    transition: all 0.3s ease;

    @media (max-width: 950px) {
      width: calc(100% - 32px);
      margin: 8px 16px 24px;
      padding: 16px;
      font-size: 16px;
    }

    background: ${colors.primary};
    color: white;

    &:hover {
      background: ${colors.hover};
      ${isBundle &&
      css`
        box-shadow: 0 8px 20px rgba(0, 0, 0, 0.2);
      `}
    }
  `}
`;
